import { Colors } from '@hawkins/variables';
import {
  LabelData,
  PixelLabelsCollection,
  DimensionLabelsCollection,
} from '../types';
import { CHART_LABEL_TEXT_SIZE } from './constants';

export function drawDimensionAndPixelLabels(
  ctx: CanvasRenderingContext2D,
  pixelLabels: PixelLabelsCollection,
  dimensionLabels: DimensionLabelsCollection
): void {
  ctx.font = `${CHART_LABEL_TEXT_SIZE}px Helvetica`;

  // Draw dimension labels
  drawDimensionsLabels(ctx, dimensionLabels);

  // Draw pixel labels
  drawXAxisPixelLabels(ctx, pixelLabels);
  drawYAxisPixelLabels(ctx, pixelLabels);
  drawAnchorPointLabel(ctx, pixelLabels.anchorPoint);
}

// Helper functions to draw pixel and dimension labels

function drawLabel(ctx: CanvasRenderingContext2D, label: LabelData): void {
  const { text, coordinates, color = Colors.Black, rotation = 0 } = label;

  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.save();
  ctx.translate(coordinates.x, coordinates.y);
  ctx.rotate(rotation);
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

function drawDimensionsLabels(
  ctx: CanvasRenderingContext2D,
  dimensionLabels: DimensionLabelsCollection
): void {
  // Draw canvas dimensions text
  drawLabel(ctx, { ...dimensionLabels.canvas, color: Colors.White });

  // Draw protection dimensions text if it exists
  if (dimensionLabels.protection) {
    drawLabel(ctx, dimensionLabels.protection);
  }

  // Draw framing decision dimensions text
  drawLabel(ctx, dimensionLabels.framingDecision);

  const rotation = -Math.PI / 2; // 90 degrees counter-clockwise

  // Draw effective canvas dimensions text if it exists (rotated)
  if (dimensionLabels.effectiveCanvas) {
    drawLabel(ctx, {
      ...dimensionLabels.effectiveCanvas,
      color: Colors.White,
      rotation,
    });
  }
}

function drawXAxisPixelLabels(
  ctx: CanvasRenderingContext2D,
  pixelLabels: PixelLabelsCollection
): void {
  const rotation = -Math.PI / 2; // 90 degrees counter-clockwise

  // Draw canvas left/right labels
  drawLabel(ctx, {
    ...pixelLabels.canvas.left,
    rotation,
    color: Colors.White,
  });

  drawLabel(ctx, {
    ...pixelLabels.canvas.right,
    rotation,
    color: Colors.White,
  });

  // Draw framing decision right label
  drawLabel(ctx, {
    ...pixelLabels.framingDecision.right,
    rotation,
  });

  // Draw effective canvas left/right labels if they exist
  if (pixelLabels.effectiveCanvas?.left) {
    drawLabel(ctx, {
      ...pixelLabels.effectiveCanvas.left,
      rotation,
      color: Colors.White,
    });
  }

  if (pixelLabels.effectiveCanvas?.right) {
    drawLabel(ctx, {
      ...pixelLabels.effectiveCanvas.right,
      rotation,
      color: Colors.White,
    });
  }

  // Draw protection left/right labels if they exist
  if (pixelLabels.protection?.left) {
    drawLabel(ctx, {
      ...pixelLabels.protection.left,
      rotation,
    });
  }

  if (pixelLabels.protection?.right) {
    drawLabel(ctx, {
      ...pixelLabels.protection.right,
      rotation,
    });
  }
}

function drawYAxisPixelLabels(
  ctx: CanvasRenderingContext2D,
  pixelLabels: PixelLabelsCollection
): void {
  // Draw canvas top/bottom labels
  drawLabel(ctx, {
    ...pixelLabels.canvas.top,
    color: Colors.White,
  });

  drawLabel(ctx, {
    ...pixelLabels.canvas.bottom,
    color: Colors.White,
  });

  // Draw framing decision bottom label
  drawLabel(ctx, {
    ...pixelLabels.framingDecision.bottom,
  });

  // Draw effective canvas top/bottom labels if they exist
  if (pixelLabels.effectiveCanvas?.top) {
    drawLabel(ctx, {
      ...pixelLabels.effectiveCanvas.top,
      color: Colors.White,
    });
  }

  if (pixelLabels.effectiveCanvas?.bottom) {
    drawLabel(ctx, {
      ...pixelLabels.effectiveCanvas.bottom,
      color: Colors.White,
    });
  }

  // Draw protection top/bottom labels if they exist
  if (pixelLabels.protection?.top) {
    drawLabel(ctx, {
      ...pixelLabels.protection.top,
    });
  }

  if (pixelLabels.protection?.bottom) {
    drawLabel(ctx, {
      ...pixelLabels.protection.bottom,
    });
  }
}

function drawAnchorPointLabel(
  ctx: CanvasRenderingContext2D,
  anchorLabel: LabelData
): void {
  drawLabel(ctx, {
    ...anchorLabel,
  });
}
